import {
    AGENT_PROVIDER_SUBSCRIPTION_PLANS,
    AGENT_SUBSCRIPTION_PRICES,
    type AgentConnection,
    type AgentProvider,
    type AgentSubscription,
    type AgentSubscriptionPlan,
} from '@/api'
import { AGENT_PROVIDER_NAMES, AGENT_PROVIDERS, hasSubscription, type SubscriptionProvider } from '@/lib/agents'
import { SUBSCRIPTION_PLAN_NAMES } from '@/lib/cost'

/**
 * What the monthly cost estimate is built from: the company's AI-account connections (null when they could not be
 * read), which account the teammate runs on, how many teammates are being added, and the daily spend cap in dollars.
 */
export type MonthlyCostInput = {
    connections: AgentConnection[] | null
    aiAccount: 'shared' | 'own'
    teammates: number
    dailyCapUsd: number | string
}

/**
 * A month, for every "up to" / "at most" figure: the daily cap times this many days. The cap resets every UTC day, so
 * 31 days is the true ceiling in every month (#1723, CTO decision 14, which revises decision 4's 30).
 */
const DAYS_A_MONTH = 31

/** "From $20" is the cheapest list price; the plan the person picks when connecting sets the real one. */
const PLAN_DECIDES = 'The price depends on the plan you choose.'

/** Who a seat is bought from, as the AI accounts panel names it: an OpenAI login is a ChatGPT seat. */
const SEAT_BRANDS: Record<SubscriptionProvider, string> = { claude: 'Claude', openai: 'ChatGPT', grok: 'Grok' }

const WHOLE_DOLLARS = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })
const DOLLARS_AND_CENTS = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Cents as a person reads dollars: `$310`, `$12.50`, `$0.31`. Never a currency code. */
function dollars(cents: number): string {
    return `$${cents % 100 === 0 ? WHOLE_DOLLARS.format(cents / 100) : DOLLARS_AND_CENTS.format(cents / 100)}`
}

/**
 * The daily cap in whole cents, rounded as the forms round it before sending, or null when it is not a positive
 * figure (blank, text, zero, negative, not finite). A null cap is never printed.
 */
function capCents(dailyCapUsd: number | string): number | null {
    const cents = Math.round(Number(dailyCapUsd) * 100)
    return Number.isFinite(cents) && cents > 0 ? cents : null
}

/** A seat's known monthly price in cents, or null when nothing prices it (so no `$0` or `$null` is printed). */
function seatPriceCents(subscription: AgentSubscription | null): number | null {
    const cents = Math.round(Number(subscription?.monthlyUsdCents))
    return subscription?.monthlyUsdCents != null && Number.isFinite(cents) && cents > 0 ? cents : null
}

/** The provider's cheapest list price a month, in cents, or null when it sells no listed plan. */
function cheapestSeatCents(provider: SubscriptionProvider): number | null {
    const plans: readonly AgentSubscriptionPlan[] = AGENT_PROVIDER_SUBSCRIPTION_PLANS[provider]
    const prices = plans
        .map(plan => AGENT_SUBSCRIPTION_PRICES[plan])
        .filter((price): price is number => typeof price === 'number' && price > 0)
    return prices.length ? Math.min(...prices) : null
}

const providerRank = (provider: AgentProvider) => {
    const rank = AGENT_PROVIDERS.indexOf(provider)
    return rank === -1 ? AGENT_PROVIDERS.length : rank
}

/**
 * The company's own connections a run could use, in the order a run tries them: provider order, then priority (CTO
 * decision 5). A teammate's own connections never count, and neither does one whose credential cannot be read.
 */
function runOrder(connections: AgentConnection[]): AgentConnection[] {
    return connections
        .filter(connection => connection.layer === 'enterprise' && connection.status === 'connected')
        .sort(
            (left, right) =>
                providerRank(left.provider) - providerRank(right.provider) ||
                left.priority - right.priority ||
                left.id.localeCompare(right.id),
        )
}

/** A subscription login bills a seat; an API key, and anything on OpenRouter, bills per use. */
function billsSeat(connection: AgentConnection): connection is AgentConnection & { provider: SubscriptionProvider } {
    return connection.authKind === 'oauth' && hasSubscription(connection.provider)
}

/** A pay-as-you-go connection as a person names it: "Claude API key", "OpenRouter key". */
function keyName(connection: AgentConnection): string {
    return connection.provider === 'openrouter'
        ? 'OpenRouter key'
        : `${AGENT_PROVIDER_NAMES[connection.provider]} API key`
}

/** A seat by its plan: "Claude Max 5x", "ChatGPT Plus", "SuperGrok" (whose plan name already says Grok). */
function seatName(provider: SubscriptionProvider, planName: string): string {
    const brand = SEAT_BRANDS[provider]
    return planName.includes(brand) ? planName : `${brand} ${planName}`
}

/** The monthly ceiling a daily cap gives, as the lines word it: "$310 a month at the $10 daily cap". */
function monthlyCeiling(cap: number): string {
    return `${dollars(cap * DAYS_A_MONTH)} a month at the ${dollars(cap)} daily cap`
}

/** The most pay-as-you-go can cost a month: the daily cap x 31, or the cap itself when there is no figure to show. */
function payAsYouGoLimit(cap: number | null): string {
    return cap === null ? 'limited by the daily spend cap' : `up to ${monthlyCeiling(cap)}`
}

/** The one line shown when the connections cannot be read: only what the cap guarantees, never a made-up seat. */
function capOnly(cap: number | null): string {
    return cap === null
        ? 'Without your AI account details, AI usage is limited by the daily spend cap. Set a cap to see it as a monthly figure.'
        : `Without your AI account details, this is the limit: at most ${dollars(cap * DAYS_A_MONTH)} a month in AI usage at the ${dollars(cap)} daily cap.`
}

/**
 * Whether the company has connected an AI account that no run can use right now: its credential cannot be read. The
 * AI accounts panel shows such a connection as "Needs attention", and the fix is to connect it again, not to set up
 * a first account.
 */
function needsAttention(connections: AgentConnection[]): boolean {
    return connections.some(connection => connection.layer === 'enterprise' && connection.status !== 'connected')
}

/** Teammates on the company's account add no seat: they share the first connection a run would try (CTO decision 2). */
function onCompanyAccount(
    usable: AgentConnection[],
    teammates: number,
    cap: number | null,
    unusable: boolean,
): string[] {
    const first = usable[0]
    if (!first && unusable) {
        return [
            cap === null
                ? "Your company's AI account needs attention: connect it again. AI usage will be limited by the daily spend cap."
                : `Your company's AI account needs attention: connect it again. Once it works, AI usage is at most ${monthlyCeiling(cap)}.`,
        ]
    }
    if (!first) {
        return [
            cap === null
                ? 'Your company has not connected an AI account yet. AI usage will be limited by the daily spend cap.'
                : `Your company has not connected an AI account yet. Once it does, AI usage is at most ${monthlyCeiling(cap)}.`,
        ]
    }
    if (!billsSeat(first)) {
        const forWhom = teammates > 1 ? ` for the ${teammates} teammates` : ''
        return [`Pay as you go on your company's ${keyName(first)}${forWhom}: ${payAsYouGoLimit(cap)}.`]
    }
    const who = teammates > 1 ? `the ${teammates} teammates share` : 'they share'
    const price = seatPriceCents(first.subscription)
    const planName = first.subscription ? SUBSCRIPTION_PLAN_NAMES[first.subscription.plan] : undefined
    const lines = [
        price !== null && planName
            ? `No new seat: ${who} your company's ${seatName(first.provider, planName)} seat (${dollars(price)} a month).`
            : `No new seat: ${who} your company's ${SEAT_BRANDS[first.provider]} subscription.`,
    ]
    // Runs fall back down the same order, so a pay-as-you-go connection behind the seat can still be spent on.
    const fallback = usable.find(connection => !billsSeat(connection))
    if (fallback) {
        lines.push(
            `Pay as you go on your company's ${keyName(fallback)} when the seat can't be used: ${payAsYouGoLimit(cap)}.`,
        )
    }
    return lines
}

/**
 * A teammate given its own account is one more seat of the provider of the company's first connection (Claude when
 * there is none), from that provider's cheapest list price (CTO decision 3). OpenRouter sells no seat, so it is the
 * pay-as-you-go limit instead.
 */
function onOwnAccount(usable: AgentConnection[], teammates: number, cap: number | null): string[] {
    const provider = usable[0]?.provider ?? 'claude'
    if (!hasSubscription(provider)) {
        const keys = teammates > 1 ? 'their own OpenRouter keys' : 'their own OpenRouter key'
        return [`Pay as you go on ${keys}: ${payAsYouGoLimit(cap)}.`]
    }
    const brand = SEAT_BRANDS[provider]
    const cheapest = cheapestSeatCents(provider)
    if (teammates > 1) {
        return [
            cheapest === null
                ? `${teammates} more ${brand} seats, one for each teammate, at the price of the plan you choose.`
                : `${teammates} more ${brand} seats, one for each teammate: from ${dollars(cheapest * teammates)} a month. ${PLAN_DECIDES}`,
        ]
    }
    return [
        cheapest === null
            ? `One more ${brand} seat for this teammate, at the price of the plan you choose.`
            : `One more ${brand} seat for this teammate: from ${dollars(cheapest)} a month. ${PLAN_DECIDES}`,
    ]
}

/**
 * The plain-words sentences that tell a person what adding teammates is expected to cost each month (#1723), from
 * what the AI account actually bills: a subscription login is a seat at its monthly price, an API key or OpenRouter is
 * pay as you go up to the daily cap x 31. AI-account cost only; the ZeroHuman plan is not part of it.
 *
 * Always at least one non-blank line. Unreadable connections give only the cap line, and nothing missing is ever
 * printed as `$null`, `$NaN`, `$0` or a guessed figure. No ids, slugs, tool names or currency codes.
 */
export function monthlyCostLines(input: MonthlyCostInput): string[] {
    const cap = capCents(input.dailyCapUsd)
    const count = Math.floor(Number(input.teammates))
    const teammates = Number.isFinite(count) && count > 1 ? count : 1
    if (!Array.isArray(input.connections)) return [capOnly(cap)]
    const usable = runOrder(input.connections)
    return input.aiAccount === 'own'
        ? onOwnAccount(usable, teammates, cap)
        : onCompanyAccount(usable, teammates, cap, needsAttention(input.connections))
}
